import React, { useCallback, useEffect, useState } from 'react';
import {
  BillingPolicyReturn,
  EstoppelReturn,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Switch } from '../common/Catalyst/switch';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatEpochDate,
  formatMoney,
  savePdfResponse,
  signedAmountParts,
} from '../utils/billingHelpers';
import { TextSetting } from './BillingPolicyFields';
import { ledgerEntryType } from './enumLabels';

interface Props {
  hoaId: string;
  homeId: string;
  canIssue: boolean;
}

const MONEY = /^\d+(\.\d{1,2})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Estoppel certificates for a home.
 *
 * Two things this screen has to say without ambiguity:
 *
 * **Issuing charges a fee to the homeowner's ledger.** Every time. It
 * is a real billable event, not a preview, so the confirmation names
 * the amount and where it lands.
 *
 * **The stored snapshot is binding and does not move.** What a
 * certificate stated is what the association can collect at closing;
 * re-rendering one from today's ledger would quietly produce a
 * different document under the same id. So the figures shown are the
 * ones stored at issue, even where the account has changed since.
 */
const EstoppelPanel: React.FC<Props> = ({ hoaId, homeId, canIssue }) => {
  const [requests, setRequests] = useState<EstoppelReturn[]>([]);
  const [policy, setPolicy] = useState<BillingPolicyReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const [issuing, setIssuing] = useState(false);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [asOf, setAsOf] = useState('');
  const [rush, setRush] = useState(false);
  const [feeOverride, setFeeOverride] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, pol] = await Promise.all([
        billingService.listEstoppel({ homeId, perPage: 50 }),
        billingService.getHoaPolicy({ hoaId }),
      ]);
      setRequests(list.data.requests);
      setPolicy(pol.data);
    } catch (err) {
      console.error('Error loading estoppel:', err);
      setError(billingErrorMessage(err, "Couldn't load certificates."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const standardFee = rush ? policy?.estoppel_fee_rush : policy?.estoppel_fee;
  const feeToCharge = feeOverride.trim() || standardFee;

  const issue = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (asOf && !DATE.test(asOf)) {
      found.asOf = 'Use a date, or leave blank for today.';
    }
    if (feeOverride.trim() && !MONEY.test(feeOverride.trim())) {
      found.fee = 'Enter an amount, or leave blank for the standard fee.';
    }
    if (email.trim() && !email.includes('@')) {
      found.email = 'That does not look like an email address.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setBusy(true);
      await billingService.issueEstoppel({
        homeId,
        estoppelRequestCreate: {
          requester_name: name.trim() || null,
          requester_company: company.trim() || null,
          requester_email: email.trim() || null,
          as_of_date: asOf || null,
          rush,
          fee_override: feeOverride.trim() || null,
        },
      });
      setIssuing(false);
      setName('');
      setCompany('');
      setEmail('');
      setAsOf('');
      setRush(false);
      setFeeOverride('');
      await load();
    } catch (err) {
      console.error('Error issuing estoppel:', err);
      setErrors({ form: billingErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const deliver = async (e: EstoppelReturn): Promise<void> => {
    try {
      setBusy(true);
      await billingService.deliverEstoppel({
        estoppelId: e.id,
        estoppelDeliverRequest: { method: 'email' },
      });
      await load();
    } catch (err) {
      console.error('Error marking delivered:', err);
      setError(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const download = async (e: EstoppelReturn): Promise<void> => {
    try {
      setDownloading(e.id);
      const response = await billingService.estoppelPdf(
        { estoppelId: e.id },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, `estoppel-${e.id}.pdf`);
    } catch (err) {
      console.error('Error downloading estoppel:', err);
      setError(billingErrorMessage(err, "Couldn't download it."));
    } finally {
      setDownloading(null);
    }
  };

  if (loading && requests.length === 0) return <LoadingSpinner />;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Estoppel"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={12000}
            isInline
          />
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Estoppel certificates</Subheading>
          <Text className="mt-1">
            The statement of this home&rsquo;s financial position that a title
            company asks for at closing. Generated from the ledger, and binding
            as issued.
          </Text>
        </div>
        {canIssue && (
          <Button color="sky" onClick={() => setIssuing(true)}>
            Issue a certificate
          </Button>
        )}
      </div>

      {requests.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>No certificate has been issued for this home.</Text>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {requests.map((e) => {
            const expired = e.good_through < today;
            return (
              <div
                key={e.id}
                className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-lg font-semibold text-zinc-900 dark:text-white">
                        {formatMoney(e.total_outstanding)}
                      </span>
                      <Badge color={expired ? 'zinc' : 'lime'}>
                        {expired ? 'Expired' : 'Good'} through{' '}
                        {formatBillingDate(e.good_through)}
                      </Badge>
                      {e.delivered_at && (
                        <Badge color="sky">
                          Delivered {formatEpochDate(e.delivered_at)}
                        </Badge>
                      )}
                    </div>
                    <Text className="mt-1 text-sm">
                      As of {formatBillingDate(e.as_of_date)}
                      {e.requester_company || e.requester_name
                        ? ` · for ${[e.requester_name, e.requester_company]
                            .filter(Boolean)
                            .join(', ')}`
                        : ''}
                      {e.fee_charged
                        ? ` · ${formatMoney(e.fee_charged)} fee charged`
                        : ''}
                    </Text>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      outline
                      onClick={() => download(e)}
                      disabled={downloading === e.id}
                    >
                      {downloading === e.id ? 'Preparing…' : 'Download PDF'}
                    </Button>
                    {canIssue && !e.delivered_at && (
                      <Button plain onClick={() => deliver(e)} disabled={busy}>
                        Mark delivered
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-3 max-w-md">
                  {e.figures.lines.map((l) => {
                    const parts = signedAmountParts(l.amount);
                    return (
                      <div
                        key={l.category}
                        className="flex justify-between gap-4 py-1 text-sm"
                      >
                        <span className="text-zinc-500 dark:text-zinc-400">
                          {ledgerEntryType.label(l.category)}
                        </span>
                        <span
                          className={
                            parts.isCredit
                              ? 'text-lime-700 dark:text-lime-400'
                              : 'text-zinc-900 dark:text-white'
                          }
                        >
                          {parts.text}
                        </span>
                      </div>
                    );
                  })}
                  <div className="mt-1 flex justify-between gap-4 border-t border-zinc-200 dark:border-zinc-700 pt-1 text-sm font-medium">
                    <span>Total</span>
                    <span>{formatMoney(e.figures.total)}</span>
                  </div>
                </div>

                <Text className="mt-3 text-xs">
                  These are the figures as they stood when this was issued. They
                  do not move, even where the account has — that is what makes
                  the certificate binding.
                </Text>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={issuing} onClose={() => setIssuing(false)} size="2xl">
        <DialogTitle>Issue an estoppel certificate</DialogTitle>
        <DialogBody>
          {errors.form && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="Nothing was issued"
                alertMessage={errors.form}
                isVisible
                onClose={() => setErrors((x) => ({ ...x, form: '' }))}
                timeout={0}
                isInline
              />
            </div>
          )}

          <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4">
            <Text>
              <strong>
                This charges {feeToCharge ? formatMoney(feeToCharge) : 'a fee'}{' '}
                to this home&rsquo;s ledger.
              </strong>{' '}
              Issuing a certificate is a billable event every time, and the
              charge is collected at closing with everything else. Several
              states cap what may be charged.
            </Text>
          </div>

          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Requester</Label>
                <Description>
                  Who asked for it — usually the title or escrow company.
                  Recorded as contact detail, not as a user of ours.
                </Description>
                <Input
                  value={name}
                  placeholder="Name"
                  onChange={(ev) => setName(ev.target.value)}
                />
              </Field>
              <Field>
                <Label>Company</Label>
                <Input
                  value={company}
                  onChange={(ev) => setCompany(ev.target.value)}
                />
              </Field>
              <Field>
                <Label>Email</Label>
                <Input
                  value={email}
                  invalid={!!errors.email}
                  onChange={(ev) => setEmail(ev.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>

              <Field>
                <Label>As of</Label>
                <Description>
                  The date the figures are stated for — usually the closing
                  date. Blank means today.
                </Description>
                <Input
                  type="date"
                  value={asOf}
                  invalid={!!errors.asOf}
                  onChange={(ev) => setAsOf(ev.target.value)}
                  className="max-w-[14rem]"
                />
                {errors.asOf && <ErrorMessage>{errors.asOf}</ErrorMessage>}
              </Field>

              <Field className="flex items-start justify-between gap-4">
                <div>
                  <Label>Rush</Label>
                  <Description>
                    {policy?.estoppel_fee_rush
                      ? `Charges the rush fee of ${formatMoney(policy.estoppel_fee_rush)} instead.`
                      : 'No rush fee is configured for this association.'}
                  </Description>
                </div>
                <Switch checked={rush} onChange={setRush} aria-label="Rush" />
              </Field>

              <TextSetting
                label="Fee"
                help={
                  standardFee
                    ? `Blank charges the standard ${formatMoney(standardFee)}.`
                    : 'This association has no estoppel fee configured.'
                }
                prefix="$"
                placeholder={standardFee ?? 'None'}
                value={feeOverride}
                error={errors.fee}
                onChange={setFeeOverride}
              />

              <Text>
                The figures are taken from the ledger and stored. They will
                stand as issued even if the account changes afterwards, and the
                certificate expires after{' '}
                {policy?.estoppel_good_through_days ?? '—'} days — dues keep
                accruing, so a figure without an expiry is wrong the day after.
              </Text>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIssuing(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="sky" onClick={issue} disabled={busy}>
            {busy
              ? 'Issuing…'
              : feeToCharge
                ? `Issue and charge ${formatMoney(feeToCharge)}`
                : 'Issue certificate'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EstoppelPanel;
